// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what the AYNI chatbot may do on this site, read by the page and by
 * the server (`functions/api/ayni-chat/`), so both build the same tool list
 * (AYNI 3D's `docs/AI_CHATBOT_PLAN.md` §10, and its research and education
 * contract, `docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md` §5).
 *
 * Shapes only: no DOM, imported by the browser and by Pages Functions.
 */
import type { AgentCommand } from '../ayni3d/shared/protocol'
import type { AiChatLanguage } from '../ayni3d/shared/ai-languages'
import type { AiChatMode } from '../ayni3d/shared/ai-chat-protocol'

type Command = AgentCommand['type']

/**
 * The agent commands the page's executor carries out (`xrChatExecutor.ts`)
 * for an account with no mode (an older page during the rollout), and the
 * base of every mode's set. Left out, so the model never sees them:
 * `sphere.slice` (no slices here) and `view.rotation` (the Tools menu owns
 * the rotate switch's state).
 */
export const XR_CHAT_COMMANDS: ReadonlySet<Command> = new Set<Command>([
  'catalog.search',
  'dataset.load',
  'view.goto',
  'view.zoom',
  'media.play',
  'media.pause',
  'media.stop',
  'media.seek',
  'media.seekDate',
  'media.speed',
  'state.describe',
  'luma.probe',
  'luma.probeMany',
  // AYNI (owner, 2026-10-06): the Analyze panel's statistics, answered from its frame and shown there.
  'luma.stats',
  'luma.extremum',
  'luma.transect',
  'luma.zonal',
  // The Data tab's measurement pins.
  'pins.place',
  'pins.read',
  'pins.clear',
  'pins.extremes',
  // AYNI (DSA 1.8, 2026-10-12): the records a vector layer draws (the quakes' magnitude, depth, place).
  'features.query',
  // AYNI (satellites ↔ catalog plan, 2026-10-24): the Satellites layer and the catalog as one graph.
  'satellites.sources',
  'satellites.datasets',
  'satellites.show',
  'satellites.passes',
  'satellites.events',
  // AYNI (owner, 2026-11-05): Solar System mode, shown, visited, laid out, timed and left (xrChatSolar.ts).
  'solar.open',
  'solar.visit',
  'solar.view',
  'solar.close',
])

/** Contract §4.2 and `explain.value`: for everyone with the chatbot. */
export const XR_GENERAL_EXTRA: readonly Command[] = ['cite.view', 'explain.value']

/** Contract §4.1: the Research environment's commands (plans with `data` and `research`). */
export const XR_RESEARCH_COMMANDS: readonly Command[] = [
  'stats.trend',
  'stats.emergence',
  'stats.correlation',
  'baseline.hold',
  'baseline.status',
  'baseline.clear',
  'change.show',
  'compare.run',
  'threshold.area',
  'hovmoller.summary',
  'methods.get',
  'figure.open',
  'bundle.open',
]

/** What a chat runs as, from `GET /api/ayni-chat/mode` (`xrChatMode.ts` resolves it). */
export interface XrChatModeInfo {
  /** Null: no mode (today's prompt and tools), for a page that sent no environment. */
  mode: AiChatMode | null
  /** The contract's student mode guides with questions; this site has no student mode yet, so always true. */
  socratic: boolean
  /** The plan has the research tools (`data` and `research`). */
  research: boolean
}

export const NO_MODE: XrChatModeInfo = { mode: null, socratic: true, research: false }

/**
 * The commands one mode offers (contract §5). This site resolves general and
 * research only (`xrChatMode.ts`); the contract's teacher and student modes
 * (lessons, classes) come with the Education side, so any other mode is
 * offered the general set.
 */
export function xrChatCommands(info: XrChatModeInfo): ReadonlySet<Command> {
  if (info.mode === null) return XR_CHAT_COMMANDS
  if (info.mode === 'research') return new Set([...XR_CHAT_COMMANDS, ...XR_GENERAL_EXTRA, ...XR_RESEARCH_COMMANDS])
  return new Set([...XR_CHAT_COMMANDS, ...XR_GENERAL_EXTRA])
}

/** The chat languages this site offers: its interface's (owner decision 5). */
export const XR_CHAT_LANGUAGES: readonly AiChatLanguage[] = ['en', 'es', 'ca']

/** Where the page reaches the model: the site's own guarded route, never the gateway. */
export const AYNI_CHAT_BASE_URL = '/api/ayni-chat'

/** The environment the page says it is in; the server narrows it (contract §2). */
export type XrChatEnvClaim = 'research' | 'education' | 'general'
export const XR_CHAT_ENV_HEADER = 'X-Ayni-Chat-Env'

export function parseEnvClaim(raw: string | null | undefined): XrChatEnvClaim | null {
  return raw === 'research' || raw === 'education' || raw === 'general' ? raw : null
}

/** The chat language for an interface locale: Spanish for Spanish, else English. */
export function chatLanguageFor(locale: string): AiChatLanguage {
  const l = locale.toLowerCase()
  return l.startsWith('es') ? 'es' : l.startsWith('ca') ? 'ca' : 'en'
}
